import React, { useEffect, useRef, useState } from 'react';
import { ShaderGradientCanvas, ShaderGradient } from 'shadergradient-old';
import * as THREE from 'three';

type GlobeJsonPoint = { x: number; y: number };
type GlobeJson = {
    points: GlobeJsonPoint[];
    countries?: Record<string, GlobeJsonPoint>;
};

const MAP_SIZE = {
    width: 2048 / 2,
    height: 1024 / 2,
};

const returnSphericalCoordinates = (
    latitude: number,
    longitude: number,
    globeRadius: number
) => {
    const lat = ((latitude - MAP_SIZE.width) / MAP_SIZE.width) * -180;
    const lon = ((longitude - MAP_SIZE.height) / MAP_SIZE.height) * -90;

    const radius = Math.cos((lon / 180) * Math.PI) * globeRadius;
    const x = Math.cos((lat / 180) * Math.PI) * radius;
    const y = Math.sin((lon / 180) * Math.PI) * globeRadius;
    const z = Math.sin((lat / 180) * Math.PI) * radius;

    return { x, y, z };
};

const OrbitPulseRings: React.FC<{ isMobile: boolean }> = ({ isMobile }) => {
    const canvasRef = useRef<HTMLCanvasElement>(null);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const parent = canvas.parentElement;
        if (!parent) return;

        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        let width = 0;
        let height = 0;
        let raf = 0;
        let last = performance.now();
        let time = 0;
        let dpr = 1;

        const resize = () => {
            const rect = parent.getBoundingClientRect();
            width = Math.max(1, Math.floor(rect.width));
            height = Math.max(1, Math.floor(rect.height));
            dpr = Math.min(window.devicePixelRatio || 1, 2);

            canvas.width = Math.floor(width * dpr);
            canvas.height = Math.floor(height * dpr);
            canvas.style.width = `${width}px`;
            canvas.style.height = `${height}px`;

            ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        };

        const draw = (now: number) => {
            const delta = (now - last) / 1000;
            last = now;
            time += delta;

            ctx.clearRect(0, 0, width, height);

            const centerX = width / 2;
            const centerY = height * (isMobile ? 0.345 : 0.38);

            const minSide = Math.min(width, height);
            const maxSide = Math.max(width, height);

            const globeRadius = isMobile
                ? Math.max(18, Math.min(70, minSide * 0.13 + maxSide * 0.02))
                : Math.max(28, Math.min(500, minSide * 0.13 + maxSide * 0.001));

            const orbitRadius = globeRadius * (isMobile ? 1.78 : 1.72);

            const maxRadius = orbitRadius + (
                isMobile
                    ? Math.max(globeRadius * 1.6, minSide * 0.3)
                    : Math.max(globeRadius * 1.8, minSide * 0.23)
            );

            const spacing = 1 / 3;

            for (let i = 0; i < 3; i++) {
                const progress = (time * 0.16 + i * spacing) % 1;
                const radius = orbitRadius + progress * (maxRadius - orbitRadius);
                const alpha = 0.18 * (1 - progress);

                ctx.beginPath();
                ctx.arc(centerX, centerY, radius, 0, Math.PI * 2);
                ctx.strokeStyle = `rgba(255, 255, 255, ${alpha})`;
                ctx.lineWidth = 1;
                ctx.shadowBlur = 14;
                ctx.shadowColor = `rgba(255, 255, 255, ${alpha * 0.85})`;
                ctx.stroke();

                if (progress < 0.3) {
                    ctx.beginPath();
                    ctx.arc(centerX, centerY, radius, -0.1 * Math.PI, 0.26 * Math.PI);
                    ctx.strokeStyle = `rgba(190, 214, 255, ${alpha * 0.75})`;
                    ctx.lineWidth = 1;
                    ctx.stroke();
                }

                ctx.shadowBlur = 0;
            }

            raf = requestAnimationFrame(draw);
        };

        resize();
        const observer = new ResizeObserver(resize);
        observer.observe(parent);
        raf = requestAnimationFrame(draw);

        return () => {
            cancelAnimationFrame(raf);
            observer.disconnect();
        };
    }, [isMobile]);

    return (
        <canvas
            ref={canvasRef}
            style={{
                position: 'absolute',
                inset: 0,
                width: '100%',
                height: '100%',
                pointerEvents: 'none',
                zIndex: 4,
            }}
        />
    );
};

const GlobeFromOriginalData: React.FC<{ isMobile: boolean }> = ({ isMobile }) => {
    const mountRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const mount = mountRef.current;
        if (!mount) return;

        let isDisposed = false;
        let raf = 0;

        const scene = new THREE.Scene();
        const camera = new THREE.PerspectiveCamera(60, 1, 1, 10000);

        const renderer = new THREE.WebGLRenderer({
            antialias: true,
            alpha: true,
        });

        renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.5));
        renderer.setClearColor(0x000000, 0);

        renderer.domElement.style.width = '100%';
        renderer.domElement.style.height = '100%';
        renderer.domElement.style.display = 'block';
        renderer.domElement.style.position = 'absolute';
        renderer.domElement.style.inset = '0';
        renderer.domElement.style.pointerEvents = 'none';

        mount.appendChild(renderer.domElement);

        const globeGroup = new THREE.Group();
        scene.add(globeGroup);

        const sphereTextureCanvas = document.createElement('canvas');
        sphereTextureCanvas.width = 128;
        sphereTextureCanvas.height = 128;
        const sphereCtx = sphereTextureCanvas.getContext('2d');
        if (sphereCtx) {
            sphereCtx.clearRect(0, 0, 128, 128);
        }

        const sphereTexture = new THREE.Texture(sphereTextureCanvas);
        sphereTexture.needsUpdate = true;

        const dotCanvas = document.createElement('canvas');
        dotCanvas.width = 64;
        dotCanvas.height = 64;
        const dotCtx = dotCanvas.getContext('2d');

        if (dotCtx) {
            const half = 32;
            const gradient = dotCtx.createRadialGradient(half, half, 0, half, half, half);
            gradient.addColorStop(0, 'rgba(255,255,255,1)');
            gradient.addColorStop(0.18, 'rgba(255,255,255,1)');
            gradient.addColorStop(0.42, 'rgba(255,255,255,0.92)');
            gradient.addColorStop(0.7, 'rgba(255,255,255,0.35)');
            gradient.addColorStop(1, 'rgba(0,0,0,0)');
            dotCtx.beginPath();
            dotCtx.arc(half, half, half, 0, 2 * Math.PI);
            dotCtx.fillStyle = gradient;
            dotCtx.fill();
        }

        const dotTexture = new THREE.Texture(dotCanvas);
        dotTexture.needsUpdate = true;

        let globeRadius = 100;
        let points: THREE.Points | null = null;
        let globeMesh: THREE.Mesh | null = null;
        let glowMesh: THREE.Mesh | null = null;
        let loadedData: GlobeJson | null = null;

        const createGlowMaterial = () =>
            new THREE.ShaderMaterial({
                uniforms: {
                    glowColor: { value: new THREE.Color(0xffffff) },
                },
                vertexShader: `
                    varying vec3 vNormal;
                    varying vec3 vWorldPosition;

                    void main() {
                        vNormal = normalize(normalMatrix * normal);
                        vec4 worldPosition = modelMatrix * vec4(position, 1.0);
                        vWorldPosition = worldPosition.xyz;
                        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
                    }
                `,
                fragmentShader: `
                    uniform vec3 glowColor;
                    varying vec3 vNormal;
                    varying vec3 vWorldPosition;

                    void main() {
                        vec3 viewDir = normalize(cameraPosition - vWorldPosition);
                        vec3 normal = normalize(vNormal);

                        float fresnel = 1.0 - max(0.0, dot(viewDir, normal));

                        float sideRing = smoothstep(0.84, 0.96, fresnel) - smoothstep(0.96, 0.995, fresnel);
                        float sideHalo = smoothstep(0.9, 1.0, fresnel) * 0.08;

                        float topGlow = smoothstep(0.2, 1.0, normal.y) * 0.1;
                        float bottomGlow = smoothstep(0.15, 1.0, -normal.y) * 0.5;

                        float finalGlow = sideRing * 0.45 + sideHalo + topGlow + bottomGlow;

                        gl_FragColor = vec4(glowColor, finalGlow * 0.22);
                    }
                `,
                transparent: true,
                blending: THREE.AdditiveBlending,
                depthWrite: false,
                depthTest: false,
                side: THREE.BackSide,
            });

        const clearGlobeObjects = () => {
            if (points) {
                globeGroup.remove(points);
                points.geometry.dispose();
                (points.material as THREE.Material).dispose();
                points = null;
            }

            if (globeMesh) {
                globeGroup.remove(globeMesh);
                globeMesh.geometry.dispose();
                (globeMesh.material as THREE.Material).dispose();
                globeMesh = null;
            }

            if (glowMesh) {
                globeGroup.remove(glowMesh);
                glowMesh.geometry.dispose();
                (glowMesh.material as THREE.Material).dispose();
                glowMesh = null;
            }
        };

        const rebuildGlobe = (data: GlobeJson) => {
            clearGlobeObjects();

            glowMesh = new THREE.Mesh(
                new THREE.SphereGeometry(globeRadius * 1.018, 96, 96),
                createGlowMaterial()
            );
            glowMesh.renderOrder = 3;
            globeGroup.add(glowMesh);

            globeMesh = new THREE.Mesh(
                new THREE.SphereGeometry(globeRadius - globeRadius * 0.02, 72, 72),
                new THREE.MeshBasicMaterial({
                    map: sphereTexture,
                    transparent: true,
                    opacity: 0.02,
                    color: 0x08292d,
                    depthWrite: false,
                })
            );
            globeMesh.renderOrder = 1;
            globeGroup.add(globeMesh);

            const allPoints: GlobeJsonPoint[] = [
                ...(data.points || []),
                ...Object.values(data.countries || {}),
            ];

            const geometry = new THREE.BufferGeometry();
            const positions = new Float32Array(allPoints.length * 3);

            for (let i = 0; i < allPoints.length; i++) {
                const result = returnSphericalCoordinates(
                    allPoints[i].x,
                    allPoints[i].y,
                    globeRadius
                );

                positions[i * 3] = result.x;
                positions[i * 3 + 1] = result.y;
                positions[i * 3 + 2] = result.z;
            }

            geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));

            const material = new THREE.PointsMaterial({
                map: dotTexture,
                size: isMobile
                    ? Math.max(0.85, globeRadius / 145)
                    : Math.max(1.6, globeRadius / 90),
                transparent: true,
                depthWrite: false,
                depthTest: true,
                color: 0xffffff,
                opacity: 1,
                blending: THREE.AdditiveBlending,
                sizeAttenuation: true,
            });

            points = new THREE.Points(geometry, material);
            points.renderOrder = 2;
            globeGroup.add(points);
        };

        const resize = () => {
            if (isDisposed) return;

            const width = Math.max(1, mount.clientWidth);
            const height = Math.max(1, mount.clientHeight);
            const safeSize = Math.min(width, height);

            globeRadius = isMobile
                ? Math.max(8, Math.min(46, safeSize * 0.078))
                : Math.max(18, Math.min(140, safeSize * 0.16));

            camera.aspect = width / height;
            camera.updateProjectionMatrix();

            camera.position.set(
                0,
                globeRadius * (isMobile ? 1.38 : 1.95),
                globeRadius * (isMobile ? 7.2 : 5.6)
            );

            camera.lookAt(
                0,
                globeRadius * (isMobile ? 0.38 : 0.9),
                0
            );

            globeGroup.position.y = globeRadius * (isMobile ? 1.65 : 1.65);

            renderer.setSize(width, height);

            if (loadedData) {
                rebuildGlobe(loadedData);
            }
        };

        const observer = new ResizeObserver(() => {
            resize();
        });
        observer.observe(mount);

        resize();

        const load = async () => {
            try {
                const response = await fetch(
                    'https://s3-us-west-2.amazonaws.com/s.cdpn.io/617753/globe-points.json'
                );

                const data: GlobeJson = await response.json();

                if (isDisposed) return;

                loadedData = data;
                rebuildGlobe(data);

                const animate = () => {
                    if (isDisposed) return;

                    globeGroup.rotation.y += isMobile ? 0.0018 : 0.0019;

                    renderer.render(scene, camera);
                    raf = requestAnimationFrame(animate);
                };

                animate();
            } catch {
                // если json не загрузился, просто не роняем компонент
            }
        };

        load();

        return () => {
            isDisposed = true;
            cancelAnimationFrame(raf);
            observer.disconnect();

            clearGlobeObjects();

            sphereTexture.dispose();
            dotTexture.dispose();
            renderer.dispose();

            if (renderer.domElement.parentNode === mount) {
                mount.removeChild(renderer.domElement);
            }
        };
    }, [isMobile]);

    return (
        <div
            ref={mountRef}
            style={{
                position: 'absolute',
                inset: 0,
                zIndex: 2,
                pointerEvents: 'none',
            }}
        />
    );
};

const BackgroundCanvas: React.FC = () => {
    const [isReady, setIsReady] = useState(false);
    const [isVisible, setIsVisible] = useState(false);
    const [isMobile, setIsMobile] = useState(false);

    useEffect(() => {
        const updateViewport = () => setIsMobile(window.innerWidth <= 768);

        updateViewport();

        let raf1 = 0;
        let raf2 = 0;

        raf1 = requestAnimationFrame(() => {
            setIsReady(true);
            raf2 = requestAnimationFrame(() => setIsVisible(true));
        });

        window.addEventListener('resize', updateViewport);

        return () => {
            cancelAnimationFrame(raf1);
            cancelAnimationFrame(raf2);
            window.removeEventListener('resize', updateViewport);
        };
    }, []);

    return (
        <div
            style={{
                position: 'absolute',
                inset: 0,
                zIndex: 0,
                overflow: 'hidden',
                background: '#000',
                pointerEvents: 'none',
            }}
        >
            {isReady && (
                <div
                    style={{
                        position: 'absolute',
                        inset: 0,
                        opacity: isVisible ? 1 : 0,
                        transition: 'opacity 180ms ease',
                        pointerEvents: 'none',
                    }}
                >
                    <ShaderGradientCanvas
                        key={isMobile ? 'mobile' : 'desktop'}
                        pixelDensity={1}
                        fov={45}
                        style={{
                            width: '100%',
                            height: '100%',
                            pointerEvents: 'none',
                        }}
                    >
                        <ShaderGradient
                            animate="on"
                            brightness={1}
                            cAzimuthAngle={180}
                            cDistance={isMobile ? 5 : 3.2}
                            cPolarAngle={115}
                            cameraZoom={isMobile ? 1.15 : 1.2}
                            color1="#031D22"
                            color2="#031D22"
                            color3="#000000"
                            envPreset="city"
                            grain="off"
                            lightType="3d"
                            positionX={0}
                            positionY={-0.2}
                            positionZ={0}
                            range="disabled"
                            rangeEnd={40}
                            rangeStart={0}
                            reflection={0}
                            rotationX={0}
                            rotationY={0}
                            rotationZ={190}
                            shader="defaults"
                            type="waterPlane"
                            uAmplitude={0}
                            uDensity={1.1}
                            uFrequency={5.5}
                            uSpeed={0.1}
                            uStrength={2.4}
                            uTime={0.2}
                            wireframe={false}
                        />
                    </ShaderGradientCanvas>
                </div>
            )}

            <GlobeFromOriginalData isMobile={isMobile} />
            <OrbitPulseRings isMobile={isMobile} />
        </div>
    );
};

export default BackgroundCanvas;
